const buildKey = ({ uid, formId }) =>
  `${formId}:${uid}`;

const saveToStorage = (key, data) => {
  try {
    localStorage.setItem(key, JSON.stringify({ ...data, _ts: Date.now() }));
  } catch {
    document.cookie = `${encodeURIComponent(key)}=${encodeURIComponent(JSON.stringify({ ...data, _ts: Date.now() }))}; max-age=259200; path=/; SameSite=Lax`;
  }
};

const readCookie = (key) => {
  const m = document.cookie.match(new RegExp(`(?:^|; )${encodeURIComponent(key)}=([^;]*)`));
  return m ? JSON.parse(decodeURIComponent(m[1])) : null;
};

const loadFromStorage = (key) => {
  try {
    const raw = localStorage.getItem(key);
    if (raw) return JSON.parse(raw);
  } catch {}
  return readCookie(key);
};

const clearStorage = (key) => {
  try { localStorage.removeItem(key); } catch {}
  document.cookie = `${encodeURIComponent(key)}=; max-age=0; path=/; SameSite=Lax`;
};

/**
 * DraftAutosave2
 * - fields: array of state keys to persist (e.g., ["q1","q2",...])
 * - formId: short string per form, e.g. "phq9" | "asrm" | "gad7"
 * - getStatus: fn -> the component’s current status (1 = editable)
 * - setState: bound this.setState from the component
 * - expireMs: how long until drafts are ignored/cleared (default 14 days)
 */
export default class DraftAutosave2 {
  constructor({ uid, formId, fields, getStatus, setState, getState, expireMs = 14 * 24 * 60 * 60 * 1000, coerceIn }) {
    this.key = buildKey({ uid, formId });
    this.fields = fields;
    this.getStatus = getStatus;
    this.setState = setState;
    this.getState = getState;
    this.expireMs = expireMs;
    this.coerceIn = typeof coerceIn === 'function' ? coerceIn : ((name, v) => v);

    this.beforeUnloadHandler = () => {
      if (this.getStatus() === 1) {
        // ensure we save the latest values, not "undefined"
        const current = typeof this.getState === 'function' ? this.getState() : undefined;
        this.saveFromState(current);
      }
    };
  }

  hydrate() {
    const draft = loadFromStorage(this.key);
    if (!draft) return;

    if (draft._ts && Date.now() - draft._ts > this.expireMs) {
      clearStorage(this.key);
      return;
    }

    const picked = {};
    for (const k of this.fields) {
      if (Object.prototype.hasOwnProperty.call(draft, k)) {
        //const v = draft[k];
        //picked[k] = typeof v === 'string' ? Number(v) : v; // coerce
        picked[k] = this.coerceIn(k, draft[k]);            // field-aware cast
      }
    }
    if (Object.keys(picked).length) this.setState(picked);
  }

  attach() {
    window.addEventListener('beforeunload', this.beforeUnloadHandler);
  }

  detach() {
    window.removeEventListener('beforeunload', this.beforeUnloadHandler);
  }

  // Save only allowed fields from current state
  saveFromState(currentState) {
    const state = currentState || (typeof this.getState === 'function' ? this.getState() : null);
    // If no getState supplied, rely on caller to pass currentState.
    const src = state || {};
    const data = {};
    for (const k of this.fields) data[k] = src[k];
    saveToStorage(this.key, data);
  }

  // Save partial change (e.g., inside handleChange)
  savePartial(name, value, currentState = {}) {
    const data = {};
    for (const k of this.fields) {
      data[k] = k === name ? value : currentState[k];
    }
    saveToStorage(this.key, data);
  }

  clear() {
    clearStorage(this.key);
  }
}
